import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import useAuthStore from '../store/useAuthStore'
import { AUTH_LABELS } from '../constants/labels'

const AccessState = ({ iconColorClass, title, message, helpText, iconPath }) => {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center max-w-md p-6">
        <div className={`${iconColorClass} mb-4`}>
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-600 mb-4">{message}</p>
        <p className="text-sm text-gray-500">{helpText}</p>
      </div>
    </div>
  )
}

/**
 * Componente que protege rutas requiriendo autenticación
 * Si hay un UUID en los params, intenta autenticar con él
 * Si no hay autenticación válida, redirige a la página de error
 */
export const ProtectedRoute = ({ children, sessionUuid, onSessionExpired, embedded = false }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isLoading = useAuthStore((state) => state.isLoading)
  const loginWithUUID = useAuthStore((state) => state.loginWithUUID)

  const [searchParams, setSearchParams] = useSearchParams()
  const searchParamsString = searchParams.toString()
  const urlUuid = searchParams.get('uuid')
  const uuid = sessionUuid || urlUuid
  const [isInitializing, setIsInitializing] = useState(() => !(isAuthenticated && !uuid))
  const inFlightUuidRef = useRef(null)
  const failedUuidRef = useRef(null)

  useEffect(() => {
    const initAuth = async () => {
      // Si ya está autenticado y no hay un nuevo UUID, no hacer nada
      if (isAuthenticated && !uuid) {
        setIsInitializing(false)
        return
      }

      if (!uuid) {
        setIsInitializing(false)
        return
      }

      // Evita reintentos automáticos por re-render o actualizaciones del store
      if (isLoading || inFlightUuidRef.current === uuid || failedUuidRef.current === uuid) {
        setIsInitializing(false)
        return
      }

      setIsInitializing(true)
      inFlightUuidRef.current = uuid

      const result = await loginWithUUID(uuid)

      inFlightUuidRef.current = null

      if (result.success) {
        failedUuidRef.current = null
        if (!embedded && urlUuid) {
          const params = new URLSearchParams(searchParamsString)
          params.delete('uuid')
          setSearchParams(params, { replace: true })
        }
      } else {
        failedUuidRef.current = uuid
        if (embedded && onSessionExpired) onSessionExpired()
      }

      setIsInitializing(false)
    }

    initAuth()
  }, [
    embedded,
    isAuthenticated,
    isLoading,
    loginWithUUID,
    onSessionExpired,
    searchParamsString,
    setSearchParams,
    urlUuid,
    uuid,
  ])

  // Mostrar spinner mientras carga
  if (isLoading || isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-80 border-t-primary-40" />
          </div>
          <p className="text-gray-600 text-lg font-medium">{AUTH_LABELS.LOADING}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <AccessState
        iconColorClass="text-yellow-500"
        title={AUTH_LABELS.ACCESS_RESTRICTED_TITLE}
        message={AUTH_LABELS.ACCESS_RESTRICTED_MESSAGE}
        helpText={AUTH_LABELS.ACCESS_RESTRICTED_HELP}
        iconPath="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    )
  }

  // Si está autenticado, renderizar el contenido protegido
  return children
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  sessionUuid: PropTypes.string,
  onSessionExpired: PropTypes.func,
  embedded: PropTypes.bool,
}

AccessState.propTypes = {
  iconColorClass: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  helpText: PropTypes.string.isRequired,
  iconPath: PropTypes.string.isRequired,
}
